import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import { useOrdersContext } from '@/contexts/OrderContext';
import Modal from '@/components/front-office/Modal';
import { useEffect, useState } from 'react';
import { calculateTotalVAT } from '@/helpers/vat';
import useDeliveryFees from '@/hooks/useDeliveryFees';

const Cart = ({
	buttonRoute,
	buttonText = 'Suivant',
	classes = 'mt-2',
	action = null,
	isStepper = false,
	handleResetValues = null,
	showCategoriesModal = false,
	setShowCategoriesModal = () => { },
	showNextButton = true,
	showOnlyButton = false,
	withNavigation = true,
	isNoLimit = false,
	hasMinimumAmount = false,
	id = 'cart-button',
	walletDiscount = 0
}) => {
	const navigate = withNavigation ? useNavigate() : null;
	const { cart, setCart, handleGetTotalPrice, promoApplied } = useOrdersContext();
	const { handleCalculateDeliveryFee } = useDeliveryFees();
	const [isDisabled, setIsDisabled] = useState(true);
	const [showEditCategoriesModal, setShowEditCategoriesModal] = useState(false);

	const getPriceDisplayData = () => {
		const priceData = handleGetTotalPrice();
		const realDeliveryFee = handleCalculateDeliveryFee();
		const deliveryFee = promoApplied?.discountType === 'free' ? 0 : realDeliveryFee;
		const basePrice = promoApplied && priceData.totalWithPromo >= 0 ? priceData.totalWithPromo : priceData.total;
		const hasPromoDiscount = priceData.totalWithPromo < priceData.total;
		const hasWalletDiscount = walletDiscount > 0;
		const priceAfterWallet = Math.max(0, basePrice - walletDiscount);
		const hasDiscount = hasPromoDiscount || hasWalletDiscount;
		const displayPrice = hasWalletDiscount ? priceAfterWallet : basePrice;
		const totalWithDelivery = promoApplied?.discountType === 'free' ? 0 : displayPrice + deliveryFee;
		// Include real delivery fees in originalTotalWithDelivery even for free orders to show correct TTC
		const originalTotalWithDelivery = priceData.total + realDeliveryFee;

		return {
			priceData,
			deliveryFee: realDeliveryFee,
			hasDiscount,
			displayPrice,
			totalWithDelivery,
			hasWalletDiscount,
			originalTotalWithDelivery
		};
	};

	let currentStep = cart?.categories[cart?.currentStepNb];
	const additionalCategories = cart?.categories?.filter(
		(c) => !c?.is_selected && c?.is_additional && !c?.is_done
	);

	useEffect(() => {
		if (isNoLimit) {
			currentStep = null;
			setIsDisabled(true);

		} else {
			if (!isStepper) {
				setIsDisabled(cart.menus.length !== 0);
			}

			if (currentStep && isStepper && !currentStep.is_additional) {
				setIsDisabled(
					currentStep.selected_products.reduce((acc, product) => acc + product.selected_quantity, 0) ===
					currentStep.step_quantity
				);
			}
		}

	}, [currentStep?.selected_products, currentStep?.extra_products, cart.menus]);

	useEffect(() => {
		if (isNoLimit) {
			setIsDisabled(handleGetTotalPrice().total > 0);
		}

		if (isNoLimit && hasMinimumAmount) {
			setIsDisabled((handleGetTotalPrice().total + handleCalculateDeliveryFee()) >= cart.is_no_limit_minimum_amount);
		}
	}, [handleGetTotalPrice]);

	const handleNextStep = () => {
		if (!isNoLimit && currentStep) {
			const selectedSteps = cart.categories.filter(
				(step) => ((step.is_selected && !step.is_additional) || (step.is_selected && step.is_additional) && step.selected_products.length > 0 || step.extra_products.length > 0)
			);

			if (isStepper) {
				if (additionalCategories.length === 0) {
					setShowEditCategoriesModal(true);
				}

				const condition = cart.currentStepNb < selectedSteps.length - 1;

				const nextStepId = selectedSteps.findIndex((step) => step.id === currentStep.id) + 1;

				let nextStep;

				// If the current step is not the last one from the selectedSteps array
				if (currentStep.id !== selectedSteps[selectedSteps.length - 1].id) {
					nextStep = selectedSteps[nextStepId];
				} else {
					nextStep = cart.categories[cart.currentStepNb + 1];
				}

				handleResetValues();

				setCart((prevState) => {
					return {
						...prevState,
						currentStepNb:
							condition && additionalCategories.length > 0
								? prevState.categories.findIndex((step) => step.id === nextStep.id)
								: prevState.currentStepNb,
						categories: prevState.categories.map((category) => {
							if (category.id === currentStep.id) {
								return {
									...category,
									is_done: isDisabled,
								};
							}

							return category;
						}),
						showModal:
							(!condition && typeof nextStep !== 'undefined') ||
							(!condition && additionalCategories.length === 0) ||
							(!condition && currentStep.name === 'Hallots') ||
							(condition && additionalCategories.length === 0),
					};
				});
			} else {
				// If not using a stepper, simply advance to the next step
				setCart((prevState) => ({
					...prevState,
				}));
			}
		}

		// Navigate to the appropriate route after the step change
		navigate(buttonRoute);
	};

	const handleCancelModalButton = (closeModal = false) => {
		if (
			additionalCategories[cart.modalStepIndex]?.id === additionalCategories[additionalCategories.length - 1]?.id
		) {
			if (closeModal) {
				setCart((prevState) => {
					return {
						...prevState,
						// modalStepIndex: 0,
						showModal: false,
					};
				});
			}

			// Ensure all additional categories, including the last one, are marked as done
			setCart((prevState) => {
				return {
					...prevState,
					categories: prevState.categories.map((category) => {
						if (additionalCategories.find((addCat) => addCat.id === category.id)) {
							return {
								...category,
								is_done: true, // Mark every additional category as done
							};
						}
						return category;
					}),
				};
			});

			setShowEditCategoriesModal(true);

			return;
		}

		// Handle marking all categories except the last one
		cart.categories.forEach((category) => {
			if (additionalCategories[cart.modalStepIndex]?.id === category.id) {
				setCart((prevState) => {
					return {
						...prevState,
						categories: prevState.categories.map((cat) => {
							if (cat.id === category.id) {
								return {
									...cat,
									is_done: true, // Mark this category as done
								};
							}
							return cat;
						}),
					};
				});
			}
		});
	};

	const handleExtraStepModalSubmit = () => {
		const categoryToChange = additionalCategories[cart.modalStepIndex];

		const updatedCategories = cart.categories.map((category) => {
			if (category.id === categoryToChange.id) {
				return {
					...category,
					step_quantity: 0,
					menu_quantity: 0,
					is_selected: true,
				};
			}

			return category;
		});

		setCart((prevState) => {
			return {
				...prevState,
				categories: updatedCategories,
				showModal: false,
				currentStepNb: cart.categories.findIndex((category) => categoryToChange.id === category.id),
				modalStepIndex: 0,
			};
		});
	};

	const handleEditCategory = (category) => {
		setShowEditCategoriesModal(false);

		setCart((prevState) => {
			return {
				...prevState,
				currentStepNb: cart.categories.findIndex((c) => c.id === category.id),
				categories: cart.categories.map((c) => {
					if (c.id === category.id) {
						return {
							...c,
							is_selected: true,
						};
					}

					return c;
				}),
				modalStepIndex: 0,
				showModal: false,
			};
		});
	};

	const handleValidateCart = () => {
		setCart((prevState) => {
			return {
				...prevState,
				showModal: false,
			};
		});

		return navigate('/commande/paiement');
	};

	const getVatCalculationObject = () => {
		return {
			selected_menus: cart?.menus?.map((menu) => ({
				id: menu.id,
				name: menu.name,
				quantity: menu.menu_quantity,
				price: menu.price,
				vat_id: menu?.vat_id,
			})),
			categories: cart.categories
				// .filter((category) => category.is_selected)
				.map((category) => ({
					category_id: category.id,
					name: category.name,
					total_quantity:
						category.selected_products.reduce((acc, product) => acc + product.selected_quantity, 0) +
						category.extra_products.reduce((acc, product) => acc + product.selected_quantity, 0),
					products: category.selected_products.map((product) => ({
						id: product.id,
						product_id: product.product_id,
						name: product.name,
						quantity: product.selected_quantity,
						price: product.price,
						vat_id: product.vat_id,
					})),
					extra_products: category.extra_products.map((extraProduct) => ({
						id: extraProduct.id,
						product_id: extraProduct.product_id,
						name: extraProduct.name,
						quantity: extraProduct.selected_quantity,
						price: extraProduct.price,
						vat_id: extraProduct.vat_id,
					})),
				})),
		};
	};

	return (
		<>
			{!showOnlyButton ? (
				<div className="relative col-span-1 bg-white/90 rounded-3xl shadow-2xl pb-12 pt-4 px-4 text-center flex flex-col justify-between h-full" id="cart-content">
					<div className="flex flex-col">
						<div className='mb-8'>
							<h2 className="border-b-2 border-black/.90 pb-2 text-3xl font-bold md:text-xl xl:text-3xl">Panier</h2>
						</div>

						{(cart.showModal || showEditCategoriesModal) && (
							<Modal
								handleCancelModalButton={handleCancelModalButton}
								showModal={showCategoriesModal}
								setShowModal={setShowCategoriesModal}
								withHeader={false}
								submitButtonText="Oui"
								closeButtonText="Non"
								handleSubmit={handleExtraStepModalSubmit}
								showFooterButtons={!showEditCategoriesModal}
								image={additionalCategories[cart.modalStepIndex]?.picture_url}
							>
								<div>
									<h2 className="mb-2 z-20">
										{!showEditCategoriesModal
											? `Voulez-vous ajouter des ${additionalCategories[cart.modalStepIndex]?.name} ?`
											: 'Voulez-vous modifier une / des étape(s) ?'}
									</h2>

									{showEditCategoriesModal && (
										<>
											<div
												onClick={() => (handleCancelModalButton(true), setShowEditCategoriesModal(false))}
												className="absolute right-0 top-0 w-screen h-screen z-10"
											></div>
											<div className="relative p-4 grid grid-cols-3 gap-2 mb-4 z-20">
												{cart.categories.map((category, index) => (
													<button
														onClick={() => handleEditCategory(category)}
														key={category?.id || index}
														className="font-bold transition duration-300 ease-in-out border border-primary p-4 rounded-md text-center flex items-center justify-center text-primary hover:bg-primary hover:text-white"
													>
														{category?.name}
													</button>
												))}
											</div>

											<button
												type="button"
												className="relative font-bold transition duration-300 bg-primary text-white ease-in-out border border-primary p-2 rounded-md text-center flex items-center justify-center text-primary mx-auto z-20 hover:bg-primary/90"
												onClick={handleValidateCart}
											>
												Je valide mes choix
											</button>
										</>
									)}
								</div>
							</Modal>
						)}

						<div id="cart-content" className="h-[30vh] overflow-y-auto">
							{/* Menus */}
							<div className="flex flex-col flex-grow mb-4">
								{cart?.menus?.length > 0 &&
									cart.menus.map((cartItem, index) => (
										<div key={cartItem.id || index} className="text-left ml-4 text-primary">
											<h2 className="text-md font-bold text-left text-primary">
												{cartItem.menu_quantity}x {cartItem.name}
											</h2>
										</div>
									))}
							</div>

							{/* Selected products */}
							<div className="pl-4">
								{cart?.categories?.length > 0 &&
									cart?.categories?.map((step, index) => (
										<div key={step?.id || index} className="mt-2">
											<h3 className="text-sm text-left">
												{step?.selected_products?.length > 0 && step?.selected_products[0]?.category_name}
											</h3>

											{step?.selected_products?.map((product, index) => {
												return (
													<div key={product.id || index} className="text-left ml-4">
														<h3 className="text-sm text-left">
															{product.selected_quantity}x {product.name}
														</h3>
													</div>
												);
											})}
										</div>
									))}

								{cart?.selected_products?.length > 0 && (
									<div className="mt-2">
										<h3 className="text-sm text-left">{cart?.selected_products[0]?.category_name}</h3>

										{cart?.selected_products?.map((product, index) => {
											return (
												<div key={product.id || index} className="text-left ml-4">
													<h3 className="text-sm text-left flex flex-col">
														{product.quantity}x {product.name}
													</h3>
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* Extra products */}
							{(currentStep?.extra_products?.length > 0 ||
								cart?.categories?.some((step) => step?.extra_products?.length > 0)) && (
									<div className="mt-8">
										{!isNoLimit && (
											<h4 className="italic text-sm border-b border-black/90 pb-2 text-center mb-2">
												Produits supplémentaires non inclus dans la formule:
											</h4>
										)}

										{cart?.categories?.length > 0 &&
											cart?.categories?.map((step, index) => (
												<div key={step?.id || index}>
													{step?.extra_products?.map((product, index) => {
														return (
															<div key={product.id || index} className="text-left">
																<h3 className="text-sm text-left">
																	{product.selected_quantity}x {product.name}{' '}
																	{product.price * product.selected_quantity}€
																</h3>
															</div>
														);
													})}
												</div>
											))}
									</div>
								)}
						</div>
					</div>
 
					{/* Total amount */}
					<div>
						{(() => {
							const { priceData, deliveryFee, hasDiscount, displayPrice, totalWithDelivery, hasWalletDiscount, originalTotalWithDelivery } = getPriceDisplayData();
							const basePrice = promoApplied && priceData.totalWithPromo >= 0 ? priceData.totalWithPromo : priceData.total;
							const priceAfterWallet = Math.max(0, basePrice - walletDiscount);
							const originalPrice = priceData.total;
							const originalPriceWithDelivery = originalPrice + deliveryFee;
							
							const hasPromoDiscount = priceData.totalWithPromo < priceData.total;
							const shouldShowStrikethrough = hasPromoDiscount || (hasWalletDiscount && priceAfterWallet < basePrice);
							
							const subtotalDisplay = shouldShowStrikethrough
								? <>{displayPrice.toFixed(2)} € <small className="line-through">{hasPromoDiscount ? originalPrice.toFixed(2) : basePrice.toFixed(2)} €</small></>
								: `${basePrice.toFixed(2)} €`;

							const totalDisplay = (hasPromoDiscount || hasWalletDiscount) && totalWithDelivery < originalPriceWithDelivery
								? <>{totalWithDelivery.toFixed(2)} € <small className="line-through">{hasPromoDiscount ? originalPriceWithDelivery.toFixed(2) : originalTotalWithDelivery.toFixed(2)} €</small></>
								: `${totalWithDelivery.toFixed(2)} €`;

							const promoDiscountAmount = hasPromoDiscount ? (originalPrice - basePrice) : 0;

							return (
								<>
									<p className='text-sm'>Dont TVA: {calculateTotalVAT(getVatCalculationObject())} €</p>
									<p className='text-sm'>Frais de livraison: {deliveryFee} €</p>
									{hasPromoDiscount && promoDiscountAmount > 0 && (
										<p className='text-sm text-green-600 font-medium'>
											Réduction : -{promoDiscountAmount.toFixed(2)} €
										</p>
									)}
									{hasWalletDiscount && walletDiscount > 0 && (
										<p className='text-sm text-green-600 font-medium'>
											Réduction : -{walletDiscount.toFixed(2)} €
										</p>
									)}
									<p className='font-bold'>Sous total : <span className="font-normal">{subtotalDisplay}</span></p>
									<p className='font-bold'>Total + livraison : <span className="font-normal">{totalDisplay}</span></p>
								</>
							);
						})()}

						{showNextButton && (
							<Button
								disabled={isDisabled}
								action={action}
								handleClick={handleNextStep}
								text={buttonText}
								extraClasses={classes}
								id={id}
							/>
						)}
					</div>
				</div>
			) : (
				<>
					{(cart.showModal || showEditCategoriesModal) && (
						<Modal
							handleCancelModalButton={handleCancelModalButton}
							showModal={showCategoriesModal}
							setShowModal={setShowCategoriesModal}
							withHeader={false}
							submitButtonText="Oui"
							closeButtonText="Non"
							handleSubmit={handleExtraStepModalSubmit}
							showFooterButtons={!showEditCategoriesModal}
							image={additionalCategories[cart.modalStepIndex]?.picture_url}
						>
							<div>
								<h2 className="mb-2 z-20">
									{!showEditCategoriesModal
										? `Voulez-vous ajouter des ${additionalCategories[cart.modalStepIndex]?.name} ?`
										: 'Voulez-vous modifier une / des étape(s) ?'}
								</h2>

								{showEditCategoriesModal && (
									<>
										<div
											onClick={() => (handleCancelModalButton(true), setShowEditCategoriesModal(false))}
											className="absolute right-0 top-0 w-screen h-screen z-10"
										></div>
										<div className="relative p-4 grid grid-cols-3 gap-2 mb-4 z-20">
											{cart.categories.map((category, index) => (
												<button
													onClick={() => handleEditCategory(category)}
													key={category?.id || index}
													className="font-bold transition duration-300 ease-in-out border border-primary p-4 rounded-md text-center flex items-center justify-center text-primary hover:bg-primary hover:text-white"
												>
													{category?.name}
												</button>
											))}
										</div>

										<button
											type="button"
											className="relative font-bold transition duration-300 bg-primary text-white ease-in-out border border-primary p-2 rounded-md text-center flex items-center justify-center text-primary mx-auto z-20 hover:bg-primary/90"
											onClick={handleValidateCart}
										>
											Je valide mes choix
										</button>
									</>
								)}
							</div>
						</Modal>
					)}

					<Button
						disabled={isDisabled}
						action={action}
						handleClick={handleNextStep}
						text={buttonText}
						extraClasses={classes}
					/>
				</>
			)}
		</>
	);
};

export default Cart;
